/**
 * saas-auth product-admin reseller MEMBER-ACCESS client (TMMPDM-0015; SASATH-0337 @ `9a08cf8`).
 *
 * Same surface and transport as `./tenants.ts`: the bootstrap-resolved AUTH origin,
 * `/api/product-admin/**` — bearer, CSRF and credentials come from `src/api/client.ts`.
 * Every response is the `{success,message,data}` envelope, so every call `unwrap()`s it.
 * Reseller, member and invitation ids travel only as path segments; saas-auth fences each
 * to the token's product (404 outside it). TMMPDM-0017 (MM-D-101): only members and
 * invitations stay here — the reseller itself and its GHL connection are tmm-api business
 * operations (`../tmm/admin.ts`).
 */
import { api } from '../client';
import type { ApiResponse, PagedResponse } from '../types';
import type {
  InvitationStatus,
  ResellerInvitationDto,
  ResellerMemberDto,
  ResellerMemberStatus,
  ResellerRole,
} from './types';

const BASE = '/api/product-admin/resellers';

function unwrap<T>(envelope: ApiResponse<T>): T {
  return envelope.data;
}

export interface ListResellerUsersQuery {
  page?: number;
  size?: number;
  q?: string;
}

/** `GET /resellers/{id}/users` — paged; `q` matches email or name. */
export async function listResellerUsers(
  resellerId: number,
  query?: ListResellerUsersQuery,
): Promise<PagedResponse<ResellerMemberDto>> {
  const { data } = await api.get<ApiResponse<PagedResponse<ResellerMemberDto>>>(
    `${BASE}/${resellerId}/users`,
    { params: query },
  );
  return unwrap(data);
}

/**
 * `POST /resellers/{id}/users` — 202 + the pending invitation (never the token). 409
 * `RESELLER_SUSPENDED`, `RESELLER_CONSOLE_NOT_CONFIGURED`, `RESELLER_MEMBER_EXISTS`,
 * `INVITATION_ALREADY_PENDING`.
 */
export async function inviteResellerUser(
  resellerId: number,
  body: { email: string; role: ResellerRole },
): Promise<ResellerInvitationDto> {
  const { data } = await api.post<ApiResponse<ResellerInvitationDto>>(
    `${BASE}/${resellerId}/users`,
    body,
  );
  return unwrap(data);
}

/** `PUT /resellers/{id}/users/{memberId}/role` — 409 `RESELLER_LAST_OWNER`. */
export async function changeResellerUserRole(
  resellerId: number,
  memberId: number,
  role: ResellerRole,
): Promise<ResellerMemberDto> {
  const { data } = await api.put<ApiResponse<ResellerMemberDto>>(
    `${BASE}/${resellerId}/users/${memberId}/role`,
    { role },
  );
  return unwrap(data);
}

/** `PUT /resellers/{id}/users/{memberId}/status` — 409 `RESELLER_LAST_OWNER`. */
export async function setResellerUserStatus(
  resellerId: number,
  memberId: number,
  status: ResellerMemberStatus,
): Promise<ResellerMemberDto> {
  const { data } = await api.put<ApiResponse<ResellerMemberDto>>(
    `${BASE}/${resellerId}/users/${memberId}/status`,
    { status },
  );
  return unwrap(data);
}

/** `DELETE /resellers/{id}/users/{memberId}` — 409 `RESELLER_LAST_OWNER`. */
export async function removeResellerUser(resellerId: number, memberId: number): Promise<void> {
  await api.delete<ApiResponse<unknown>>(`${BASE}/${resellerId}/users/${memberId}`);
}

/** `GET /resellers/{id}/invitations?status=` — a JSON array, newest first; never the token. */
export async function listResellerInvitations(
  resellerId: number,
  status?: InvitationStatus,
): Promise<ResellerInvitationDto[]> {
  const { data } = await api.get<ApiResponse<ResellerInvitationDto[]>>(
    `${BASE}/${resellerId}/invitations`,
    status ? { params: { status } } : undefined,
  );
  return unwrap(data);
}

/** `POST …/invitations/{invId}/resend` — rotates in place; 409 `INVITATION_ALREADY_ACCEPTED`. */
export async function resendResellerInvitation(
  resellerId: number,
  invitationId: number,
): Promise<ResellerInvitationDto> {
  const { data } = await api.post<ApiResponse<ResellerInvitationDto>>(
    `${BASE}/${resellerId}/invitations/${invitationId}/resend`,
    {},
  );
  return unwrap(data);
}

/** `DELETE …/invitations/{invId}` — idempotent; 409 `INVITATION_ALREADY_ACCEPTED`. */
export async function revokeResellerInvitation(
  resellerId: number,
  invitationId: number,
): Promise<ResellerInvitationDto> {
  const { data } = await api.delete<ApiResponse<ResellerInvitationDto>>(
    `${BASE}/${resellerId}/invitations/${invitationId}`,
  );
  return unwrap(data);
}
